import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * OVID header menu.
 *
 * Orchestrates:
 * - The desktop mega-menu (hover / focus / keyboard to open a panel, close on
 *   blur / Escape / outside click).
 * - The mobile trigger (toggles the `<header-drawer>` dialog via a custom
 *   `drawer:toggle` event).
 * - Distribution of `<template class="mega-menu-feature">` tiles from the
 *   hidden pool into the matching desktop panel feature slot and mobile
 *   drawer feature rows (matched by `data-parent-handle`).
 *
 * Dispatches `menu:open` / `menu:close` on itself (bubbles up to
 * `<header-component>`) when the desktop mega-menu is displayed.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} trigger - Hamburger trigger button.
 * @property {HTMLElement} [desktop] - Desktop menubar container.
 * @property {HTMLElement} [menubar] - The <ul role="menubar"> element.
 * @property {HTMLAnchorElement[]} [menuitem] - Individual menubar links.
 * @property {HTMLElement[]} [panel] - Desktop panels, one per link with children.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['trigger'];

  /** @type {HTMLElement | null} */
  #activePanel = null;

  /** @type {HTMLAnchorElement | null} */
  #activeMenuitem = null;

  /** @type {number | null} */
  #closeTimer = null;

  /** @type {MediaQueryList | null} */
  #desktopMQ = null;

  /**
   * Last seen pointer position. Used to keep the panel open while the cursor
   * travels (slowly, or with a pause) through the gap between a menu link and
   * its panel — a region the CSS hover bridge cannot fully cover. @type {number}
   */
  #pointerX = 0;
  /** @type {number} */
  #pointerY = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#desktopMQ = window.matchMedia('(min-width: 990px)');

    // Distribute feature tiles into their slots on first mount.
    this.#distributeFeatureTiles();

    // Desktop menubar wiring.
    const menubar = this.refs.menubar;
    if (menubar instanceof HTMLElement) {
      menubar.addEventListener('pointerenter', this.#onMenubarPointerEnter, true);
      menubar.addEventListener('pointerleave', this.#onMenubarPointerLeave);
      menubar.addEventListener('focusin', this.#onMenubarFocusIn);
      menubar.addEventListener('focusout', this.#onMenubarFocusOut);
      menubar.addEventListener('keydown', this.#onMenubarKeydown);
    }

    // Panels hold the close timer open while the pointer is over them so the
    // cursor can travel trigger → panel without the panel snapping shut.
    for (const panel of this.#allPanels()) {
      panel.addEventListener('pointerenter', this.#onPanelPointerEnter);
      panel.addEventListener('pointerleave', this.#onPanelPointerLeave);
    }

    document.addEventListener('keydown', this.#onDocumentKeydown);
    document.addEventListener('pointerdown', this.#onDocumentPointerDown);
    document.addEventListener('pointermove', this.#onDocumentPointerMove, { passive: true });

    // Reflect drawer open state on the trigger (icon swap + aria-expanded).
    this.addEventListener('menu:open', this.#onMenuOpenBubble);
    this.addEventListener('menu:close', this.#onMenuCloseBubble);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const menubar = this.refs.menubar;
    if (menubar instanceof HTMLElement) {
      menubar.removeEventListener('pointerenter', this.#onMenubarPointerEnter, true);
      menubar.removeEventListener('pointerleave', this.#onMenubarPointerLeave);
      menubar.removeEventListener('focusin', this.#onMenubarFocusIn);
      menubar.removeEventListener('focusout', this.#onMenubarFocusOut);
      menubar.removeEventListener('keydown', this.#onMenubarKeydown);
    }

    for (const panel of this.#allPanels()) {
      panel.removeEventListener('pointerenter', this.#onPanelPointerEnter);
      panel.removeEventListener('pointerleave', this.#onPanelPointerLeave);
    }

    document.removeEventListener('keydown', this.#onDocumentKeydown);
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown);
    document.removeEventListener('pointermove', this.#onDocumentPointerMove);

    this.removeEventListener('menu:open', this.#onMenuOpenBubble);
    this.removeEventListener('menu:close', this.#onMenuCloseBubble);
  }

  /**
   * @param {Event} event
   */
  #onMenuOpenBubble = (event) => {
    // Only update the trigger for drawer events (ignore our own desktop events
    // to avoid marking the whole menu as open when a panel is expanded).
    if (event.target === this) return;
    this.dataset.open = 'true';
    this.refs.trigger?.setAttribute('aria-expanded', 'true');
  };

  /**
   * @param {Event} event
   */
  #onMenuCloseBubble = (event) => {
    if (event.target === this) return;
    delete this.dataset.open;
    this.refs.trigger?.setAttribute('aria-expanded', 'false');
  };

  /**
   * Click on the hamburger trigger.
   * On narrow viewports we open the mobile drawer; on wider viewports the
   * trigger is not interactive (it collapses via CSS), but if the panels are
   * open via keyboard we still close them.
   */
  toggle = () => {
    if (this.#desktopMQ?.matches) {
      if (this.#activePanel) {
        this.#closePanel();
      }
      return;
    }

    const drawer = this.querySelector('header-drawer');
    if (!drawer) return;

    drawer.dispatchEvent(new CustomEvent('drawer:toggle', { bubbles: true }));
  };

  /* -------------------------------------------------------------------
   * Desktop mega-menu
   * ------------------------------------------------------------------- */

  /**
   * @param {PointerEvent} event
   */
  #onMenubarPointerEnter = (event) => {
    if (!this.#desktopMQ?.matches) return;
    const target = event.target;
    if (!(target instanceof Element)) return;

    const item = target.closest('[role="menuitem"]');
    if (!(item instanceof HTMLAnchorElement)) return;

    this.#openPanelFor(item);
  };

  #onMenubarPointerLeave = () => {
    if (!this.#desktopMQ?.matches) return;
    this.#scheduleClose();
  };

  /**
   * Keep the panel open while the pointer is inside it. Without this the
   * close timer started by menubar-leave would fire even though the user is
   * interacting with the panel contents.
   */
  #onPanelPointerEnter = () => {
    if (!this.#desktopMQ?.matches) return;
    this.#cancelClose();
  };

  #onPanelPointerLeave = () => {
    if (!this.#desktopMQ?.matches) return;
    this.#scheduleClose();
  };

  /**
   * @param {FocusEvent} event
   */
  #onMenubarFocusIn = (event) => {
    if (!this.#desktopMQ?.matches) return;
    const target = event.target;
    if (!(target instanceof Element)) return;

    const item = target.closest('[role="menuitem"]');
    if (item instanceof HTMLAnchorElement) {
      this.#openPanelFor(item);
    }
  };

  /**
   * @param {FocusEvent} event
   */
  #onMenubarFocusOut = (event) => {
    if (!this.#desktopMQ?.matches) return;
    const next = event.relatedTarget;
    // Keep open if focus is still inside the menubar or the active panel.
    if (next instanceof Node) {
      if (this.refs.menubar?.contains(next)) return;
      if (this.#activePanel?.contains(next)) return;
    }
    this.#closePanel();
  };

  /**
   * Keyboard model: ArrowLeft / ArrowRight move across the menubar.
   * ArrowDown / Enter / Space opens the panel and moves focus into it.
   * Escape closes the panel and restores focus to the item.
   * @param {KeyboardEvent} event
   */
  #onMenubarKeydown = (event) => {
    if (!this.#desktopMQ?.matches) return;

    const items = this.#menuItems();
    const current = document.activeElement;
    if (!(current instanceof HTMLElement)) return;
    const index = items.indexOf(/** @type {HTMLAnchorElement} */ (current));

    switch (event.key) {
      case 'ArrowRight': {
        if (index === -1) return;
        event.preventDefault();
        const next = items[(index + 1) % items.length];
        next?.focus();
        break;
      }
      case 'ArrowLeft': {
        if (index === -1) return;
        event.preventDefault();
        const prev = items[(index - 1 + items.length) % items.length];
        prev?.focus();
        break;
      }
      case 'ArrowDown':
      case 'Enter':
      case ' ': {
        if (!(current instanceof HTMLAnchorElement)) return;
        if (current.getAttribute('aria-haspopup') !== 'true') return;

        event.preventDefault();
        this.#openPanelFor(current);
        const panel = this.#activePanel;
        const firstLink = panel?.querySelector('a, button');
        if (firstLink instanceof HTMLElement) firstLink.focus();
        break;
      }
      case 'Escape': {
        if (!this.#activePanel) return;
        event.preventDefault();
        const toFocus = this.#activeMenuitem;
        this.#closePanel();
        toFocus?.focus();
        break;
      }
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onDocumentKeydown = (event) => {
    if (event.key === 'Escape' && this.#activePanel) {
      const toFocus = this.#activeMenuitem;
      this.#closePanel();
      toFocus?.focus();
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #onDocumentPointerDown = (event) => {
    if (!this.#activePanel) return;
    const target = event.target;
    if (!(target instanceof Node)) return;
    if (this.contains(target)) return;
    this.#closePanel();
  };

  /**
   * Pointer-aware keep-open guard. While a panel is open, cancel any pending
   * close as long as the pointer is anywhere in the menu zone (menubar, the
   * open panel, or the gap directly beneath the menubar); otherwise arm a
   * close. This bridges the dead zone between a link and its panel that the
   * CSS `::before` bridge can't safely cover, and — unlike the bridge — it
   * holds even when the cursor moves slowly or pauses mid-gap (ODB-108).
   * @param {PointerEvent} event
   */
  #onDocumentPointerMove = (event) => {
    this.#pointerX = event.clientX;
    this.#pointerY = event.clientY;

    if (!this.#desktopMQ?.matches || !this.#activePanel) return;

    if (this.#pointerInMenuZone(this.#pointerX, this.#pointerY)) {
      this.#cancelClose();
    } else if (this.#closeTimer == null) {
      this.#scheduleClose();
    }
  };

  /**
   * Whether (x, y) is inside the menubar, the active panel, or the gap between
   * them directly beneath the menubar (so a straight-down move from any link
   * stays "live").
   * @param {number} x
   * @param {number} y
   * @returns {boolean}
   */
  #pointerInMenuZone(x, y) {
    const menubar = this.refs.menubar;
    const panel = this.#activePanel;
    if (!(menubar instanceof HTMLElement) || !panel) return false;

    const mb = menubar.getBoundingClientRect();
    const pr = panel.getBoundingClientRect();

    const inRect = (/** @type {DOMRect} */ r) =>
      x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;

    if (inRect(mb) || inRect(pr)) return true;

    // The bridging gap: between the menubar's bottom and the panel's top,
    // within the menubar's horizontal span.
    return y >= mb.bottom && y <= pr.top && x >= mb.left && x <= mb.right;
  }

  /**
   * @param {HTMLAnchorElement} item
   */
  #openPanelFor(item) {
    if (item.getAttribute('aria-haspopup') !== 'true') {
      // A link with no children closes any open panel.
      this.#closePanel();
      return;
    }

    const id = item.getAttribute('aria-controls');
    if (!id) return;
    const panel = this.ownerDocument.getElementById(id);
    if (!(panel instanceof HTMLElement)) return;

    if (this.#activePanel === panel) {
      this.#cancelClose();
      return;
    }

    this.#cancelClose();

    // Close the previous panel.
    if (this.#activePanel) this.#hidePanel(this.#activePanel, this.#activeMenuitem);

    this.#activePanel = panel;
    this.#activeMenuitem = item;

    item.setAttribute('aria-expanded', 'true');
    panel.hidden = false;

    this.dispatchEvent(
      new CustomEvent('menu:open', { bubbles: true, composed: true })
    );
  }

  #scheduleClose() {
    this.#cancelClose();
    // 240ms gives enough headroom for the cursor to travel over the 12px
    // bridge between trigger and panel without triggering a close; drops to
    // 0 when reduced motion is requested.
    const delay = prefersReducedMotion() ? 0 : 240;
    this.#closeTimer = window.setTimeout(() => {
      // Don't close if focus is still in the menubar or active panel.
      const active = document.activeElement;
      if (active instanceof Node) {
        if (this.refs.menubar?.contains(active)) return;
        if (this.#activePanel?.contains(active)) return;
      }
      this.#closePanel();
    }, delay);
  }

  #cancelClose() {
    if (this.#closeTimer != null) {
      window.clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }

  #closePanel() {
    this.#cancelClose();
    if (!this.#activePanel) return;

    this.#hidePanel(this.#activePanel, this.#activeMenuitem);
    this.#activePanel = null;
    this.#activeMenuitem = null;

    this.dispatchEvent(
      new CustomEvent('menu:close', { bubbles: true, composed: true })
    );
  }

  /**
   * @param {HTMLElement} panel
   * @param {HTMLAnchorElement | null} item
   */
  #hidePanel(panel, item) {
    panel.hidden = true;
    item?.setAttribute('aria-expanded', 'false');
  }

  /** @returns {HTMLAnchorElement[]} */
  #menuItems() {
    const menuitem = this.refs.menuitem;
    if (Array.isArray(menuitem)) {
      return /** @type {HTMLAnchorElement[]} */ (menuitem.filter((el) => el instanceof HTMLAnchorElement));
    }
    return [];
  }

  /** @returns {HTMLElement[]} */
  #allPanels() {
    const panel = this.refs.panel;
    if (Array.isArray(panel)) {
      return panel.filter((el) => el instanceof HTMLElement);
    }
    return [];
  }

  /* -------------------------------------------------------------------
   * Feature tile distribution
   * ------------------------------------------------------------------- */

  /**
   * Distribute feature tiles from the hidden pool into their destination
   * slots:
   * - `<template class="mega-menu-feature" data-parent-handle="...">`
   *   → desktop `[data-feature-slot="<handle>"]` only (desktop mega menu).
   * - `<template class="mobile-menu-feature">`
   *   → mobile drawer `[data-feature-slot="__mobile_l1__"]` only.
   *
   * L2 mobile submenus intentionally receive no tiles.
   */
  #distributeFeatureTiles() {
    const desktopTemplates = this.querySelectorAll('template.mega-menu-feature');
    for (const template of desktopTemplates) {
      if (!(template instanceof HTMLTemplateElement)) continue;
      const handle = template.dataset.parentHandle?.trim().toLowerCase();
      if (!handle) continue;

      const desktopSlots = this.querySelectorAll(`[data-feature-slot="${cssEscape(handle)}"]`);
      for (const slot of desktopSlots) {
        if (!(slot instanceof HTMLElement)) continue;
        slot.appendChild(template.content.cloneNode(true));
        slot.removeAttribute('aria-hidden');
      }
    }

    const mobileTemplates = this.querySelectorAll('template.mobile-menu-feature');
    if (mobileTemplates.length === 0) return;

    const mobileSlots = this.querySelectorAll('[data-feature-slot="__mobile_l1__"]');
    if (mobileSlots.length === 0) return;

    for (const template of mobileTemplates) {
      if (!(template instanceof HTMLTemplateElement)) continue;
      for (const slot of mobileSlots) {
        if (!(slot instanceof HTMLElement)) continue;
        slot.appendChild(template.content.cloneNode(true));
        slot.removeAttribute('aria-hidden');
      }
    }
  }
}

/**
 * CSS.escape() fallback for older browsers.
 * @param {string} str
 * @returns {string}
 */
function cssEscape(str) {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(str);
  }
  return str.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}
